import type {Auth} from '@aws-amplify/auth'
import {AuthOptions} from '@aws-amplify/auth/lib-esm/types'

import createCognitoStorage from './cognito-storage'

let auth: Promise<typeof Auth> | undefined

export type AuthService = ReturnType<typeof create>

const create = (
  amplifyConfig: Record<string, unknown> & {Auth: AuthOptions},
  onAmplifyChunkLoadFail: () => void
) => {
  const getAuth = (): Promise<typeof Auth> => {
    if (!auth) {
      auth = new Promise<typeof Auth>(resolve => {
        if (auth) {
          resolve(auth)
          return
        }

        import('@aws-amplify/auth')
          .then(module => {
            const authModule = module.default
            authModule.configure({
              ...amplifyConfig,
              storage: createCognitoStorage(),
            })
            resolve(authModule)
          })
          .catch(() => onAmplifyChunkLoadFail())
      })
    }

    return auth
  }

  const getCurrentSession = async () => {
    try {
      const session = await getAuth().then(a => a.currentSession())
      return session
    } catch (error) {
      return null
    }
  }

  const onAuthFailAfterVisibilityChange = (onFail: () => void) => {
    document.addEventListener('visibilitychange', async () => {
      // Check if auth chunk has already been loaded otherwise the user was never logged in and we don't have to load it now.
      if (document.visibilityState === 'visible' && auth && !(await getCurrentSession())) {
        onFail()
      }
    })
  }

  return {
    getAuth,
    getCurrentSession,
    onAuthFailAfterVisibilityChange,
  }
}
export default create
